import React from 'react';
import { Users, Target, Zap, Palette, BarChart3, Heart, Globe, Sparkles, Shield, Rocket } from 'lucide-react';

const Team = () => {
  const teamMembers = [
    {
      name: "Shaikh Samiha",
      role: "Founder & Community Lead",
      description: "Visionary building a structured ecosystem for student growth and professional development.",
      icon: <Target className="w-5 h-5" />,
      image: "/team-samiha.jpg"
    },
    {
      name: "Adiba Kauser",
      role: "Community Operations",
      description: "Ensuring seamless coordination and execution of community initiatives.",
      icon: <Zap className="w-5 h-5" />,
      image: "/team-adiba.jpg"
    },
    {
      name: "Mohsin Khan",
      role: "Content & Brand Strategy",
      description: "Crafting authentic narratives that connect people and build digital presence.",
      icon: <Globe className="w-5 h-5" />,
      image: "/team-mohsin.jpg"
    },
    {
      name: "Saad Thara",
      role: "HR & Student Relations",
      description: "Fostering structured growth and healthy collaboration within the community.",
      icon: <Heart className="w-5 h-5" />,
      image: "/team-saad.jpg"
    },
    {
      name: "Vikas Chitrak",
      role: "Marketing & Growth",
      description: "Driving community expansion through strategic outreach and engagement.",
      icon: <BarChart3 className="w-5 h-5" />,
      image: "/team-vikas.jpg"
    },
    {
      name: "Siddhi",
      role: "Graphic Design & Branding",
      description: "Shaping visual identity through creative and consistent design assets.",
      icon: <Palette className="w-5 h-5" />,
      image: "/team-siddhi.jpg"
    }
  ];

  const teamValues = [
    {
      title: "Collaboration",
      description: "Working together to achieve shared goals",
      icon: <Users className="w-6 h-6" />
    },
    {
      title: "Integrity",
      description: "Honest and transparent in all interactions",
      icon: <Shield className="w-6 h-6" />
    },
    {
      title: "Innovation",
      description: "Constantly improving and adapting",
      icon: <Sparkles className="w-6 h-6" />
    },
    {
      title: "Growth",
      description: "Focused on continuous learning",
      icon: <Rocket className="w-6 h-6" />
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-[#F4FAF9] to-white py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-[#0C7779] rounded-2xl mb-8">
              <Users className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-[#0F172A] leading-tight">
              Our Core Team
            </h1>
            <p className="text-xl text-[#64748B] max-w-3xl mx-auto">
              A dedicated group of builders working to create meaningful impact through education and community.
            </p>
          </div>
        </div>
      </div>

      {/* Team Values */}
      <div className="py-12 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-center mb-12 text-[#0F172A]">Our Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamValues.map((value, index) => (
              <div key={index} className="text-center p-6">
                <div className="w-14 h-14 bg-[#3BC1A8]/10 rounded-2xl flex items-center justify-center mx-auto mb-4 text-[#0C7779]">
                  {value.icon}
                </div>
                <h3 className="text-lg font-bold text-[#0F172A] mb-2">{value.title}</h3>
                <p className="text-[#64748B] text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Team Members with Real Images */}
      <div className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4 text-[#0F172A]">Meet the Team</h2>
            <p className="text-[#64748B] max-w-2xl mx-auto">
              Each member brings unique expertise to build a comprehensive learning ecosystem.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {teamMembers.map((member, index) => (
              <div 
                key={index} 
                className="bg-white rounded-xl border border-[#E2EFEA] p-6 hover:border-[#249E94]/40 hover:shadow-sm transition-all duration-300"
              >
                {/* Team Member Image */}
                <div className="relative h-48 bg-gradient-to-br from-[#F4FAF9] to-white rounded-lg mb-6 overflow-hidden">
                  <img 
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // Fallback if image doesn't load
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = `
                        <div class="absolute inset-0 flex flex-col items-center justify-center">
                          <div class="w-12 h-12 bg-[#3BC1A8]/20 rounded-full flex items-center justify-center mb-3">
                            ${member.icon.props.className.includes('Target') ? '<svg class="w-6 h-6 text-[#0C7779]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>' : 
                            member.icon.props.className.includes('Zap') ? '<svg class="w-6 h-6 text-[#0C7779]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>' :
                            member.icon.props.className.includes('Globe') ? '<svg class="w-6 h-6 text-[#0C7779]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg>' :
                            '<div class="w-6 h-6 bg-[#0C7779] rounded-full"></div>'}
                          </div>
                          <span class="text-sm text-[#64748B]">${member.name}</span>
                          <span class="text-xs text-[#64748B]">Team Member</span>
                        </div>
                      `;
                    }}
                  />
                </div>

                {/* Content */}
                <h3 className="text-xl font-bold text-[#0F172A] mb-2">{member.name}</h3>
                <div className="mb-4">
                  <span className="inline-block px-3 py-1.5 bg-[#0C7779] text-white rounded-full text-xs font-medium">
                    {member.role}
                  </span>
                </div>
                <p className="text-[#64748B] text-sm leading-relaxed">{member.description}</p>
                
                {/* Icon at bottom */}
                <div className="mt-6 w-10 h-10 bg-[#3BC1A8]/10 rounded-lg flex items-center justify-center text-[#0C7779]">
                  {member.icon}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mission Section */}
      <div className="py-16 px-4 bg-gradient-to-b from-white to-[#F4FAF9]">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="bg-white rounded-2xl p-8 md:p-12 border border-[#E2EFEA]">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-[#0C7779] rounded-2xl mb-6">
              <Sparkles className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-2xl md:text-3xl font-bold mb-6 text-[#0F172A]">Our Collective Mission</h3>
            <p className="text-[#64748B] leading-relaxed max-w-3xl mx-auto">
              We're building more than a community—we're creating an ecosystem where students can 
              find direction, mentors can share wisdom, and everyone grows together through 
              authentic connections and practical learning.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <span className="px-4 py-2 bg-[#F4FAF9] text-[#0C7779] text-sm rounded-full">Impact-Driven</span>
              <span className="px-4 py-2 bg-[#F4FAF9] text-[#0C7779] text-sm rounded-full">Student-Focused</span>
              <span className="px-4 py-2 bg-[#F4FAF9] text-[#0C7779] text-sm rounded-full">Community-First</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Team;